import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const routeHistoryMax = 50;

export interface RouteHistoryEntry {
  route: string;
  params?: { [key: string]: string };
  title?: string;
}

interface OpportunitySettings {
  showOpportunityDrawer: boolean;
  showCustomerForm: boolean;
}

/**
 * Session-recovery state machine, driven by a rejected token (401) mid-use:
 *  - 'idle'       — a live session, nothing to recover.
 *  - 'attempting' — a silent re-auth is in flight (backend cookie session restore). The app is
 *                   covered by an opaque overlay so no sensitive data is shown while it runs.
 *  - 'failed'     — silent re-auth could not restore the session; the blocking login overlay is
 *                   shown so the user (or whoever is at the keyboard) must re-authenticate before
 *                   any data is visible again. The page and route stay mounted underneath.
 */
export type SessionRecoveryStatus = 'idle' | 'attempting' | 'failed';

export interface UiSettings extends OpportunitySettings {
  showReloginModal: boolean;
  sessionRecovery: SessionRecoveryStatus;
  minimizeMenu: boolean;
  showGenericModal: boolean;
  routeHistory: RouteHistoryEntry[];
}

const initialState: UiSettings = {
  showOpportunityDrawer: false,
  showCustomerForm: false,
  showReloginModal: false,
  sessionRecovery: 'idle',
  minimizeMenu: false,
  showGenericModal: false,
  routeHistory: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setOpportunityUi(
      state,
      action: PayloadAction<{ uiSettings: Partial<UiSettings> }>
    ) {
      return { ...state, ...action.payload.uiSettings }; // Use return to ensure state updates!
    },
    setOpportunityUiValue(
      state,
      action: PayloadAction<{
        property: keyof OpportunitySettings;
        value: boolean;
      }>
    ) {
      const { property, value } = action.payload;
      state[property] = value;
    },
    showReloginModal(state) {
      state.showReloginModal = true;
    },
    hideReloginModal(state) {
      // Every place that dismisses the re-auth prompt (logout, a successful OAuth callback,
      // clearing auth state) also ends recovery, so the overlay never lingers over a fresh
      // session or the login screen.
      state.showReloginModal = false;
      state.sessionRecovery = 'idle';
    },
    // A 401 was seen and a silent re-auth is being attempted; cover the app but do not yet
    // surface the login prompt.
    beginSessionRecovery(state) {
      state.sessionRecovery = 'attempting';
      state.showReloginModal = false;
    },
    // Silent re-auth could not restore the session; surface the blocking login overlay.
    sessionRecoveryFailed(state) {
      state.sessionRecovery = 'failed';
      state.showReloginModal = true;
    },
    // Silent re-auth succeeded (or recovery was otherwise resolved); return to a live session.
    endSessionRecovery(state) {
      state.sessionRecovery = 'idle';
      state.showReloginModal = false;
    },
    appendRoute(state, action: PayloadAction<RouteHistoryEntry>) {
      const newHistory = state.routeHistory
        .filter((route) => route.route !== action.payload.route);
      newHistory.push(action.payload);
      state.routeHistory = newHistory.slice(-routeHistoryMax);
    },
    setMinimizedMenu(state, action: PayloadAction<boolean>) {
      state.minimizeMenu = action.payload;
    },
    openGenericModal(state) {
      state.showGenericModal = true;
    },
    closeGenericModal(state) {
      state.showGenericModal = false;
    },
  },
});

export const {
  setOpportunityUi,
  setOpportunityUiValue,
  showReloginModal,
  hideReloginModal,
  beginSessionRecovery,
  sessionRecoveryFailed,
  endSessionRecovery,
  appendRoute,
  setMinimizedMenu,
  openGenericModal,
  closeGenericModal,
} = uiSlice.actions;
export default uiSlice.reducer;
